// The two shapes this screen reads, normalized into one row.
//
// They are NOT the same shape, and that is the whole reason this file exists:
//
//   HTTP  GET /admin/events   → { id, kind, project_id, tier_id, task_id,
//                                  user_id, token_id, data, createdAt }
//   SSE   the notice (T15.5)   → { id, kind, at, project_id, tier_id, task_id,
//                                  repository_id?, task?: { id, title?, state } }
//
// THE NOTICE IS NOT THE EVENT. It travels without `data` and WITHOUT AN AUTHOR,
// under 300 bytes; the title is cut, or dropped, to fit. Whoever needs the rest
// asks `GET /admin/events/{id}` — which is what `authorOf` in contexts/live
// does. The row's field names stay as a fallback, so a replay served verbatim
// from `events` still reads.

export const STREAM_STATUS = {
    idle:       'idle',
    connecting: 'connecting',
    live:       'live',
    retrying:   'retrying',
    down:       'down',
};

// How many failed connection attempts before the channel is declared down.
//
// EventSource retries on its own, forever, with no backoff we can reach from
// here. Left alone against a route that answers 404 it is an infinite loop at
// three seconds a turn. Counting them and closing at the cap is the only lever
// the browser gives: after this, reconnecting is a decision somebody takes.
export const MAX_ATTEMPTS = 5;

// The channel's way of saying "you are too far behind to replay, ask again".
// T15.2 publishes it as a named SSE event, `recargar`, carrying the
// `last_event_id` it gave up on.
//
// The belt underneath it stays: recovering from a dead channel always reloads
// over HTTP before reopening, so a missed one costs a stale list, never a
// wrong one.
export const RESYNC_EVENT = 'recargar';

// How many rows the pulse holds. Older ones fall off the bottom: this is a
// pulse, not an archive — the archive is GET /admin/events.
export const FEED_SIZE = 50;

// The movements of a task, which are the ones the system exists to move.
// They come from EventRecorder in mastermind_core; the CRUD ones (created,
// updated, deactivated) are labelled by rule below instead of one by one.
const TASK_KIND_LABEL = {
    'task.authorized': 'Autorizó',
    'task.unblocked': 'Desbloqueó',
    'task.claimed':   'Reservó',
    'task.progress':  'Avanza',
    'task.delivered': 'Entregó',
    'task.accepted':  'Aceptó',
    'task.returned':  'Devolvió',
    'task.blocked':   'Bloqueó',
    'task.promoted':  'Promovió',
    'task.released':  'Liberó',
    'task.discarded': 'Descartó',
};

const VERB_LABEL = {
    created:     'creó',
    updated:     'actualizó',
    deactivated: 'dio de baja',
    promoted:    'promovió',
    discarded:   'descartó',
    released:    'liberó',
    assigned:    'asignó',
    removed:     'quitó',
    published:   'publicó',
    dependency_added: 'ató una dependencia en',
    opened:      'abrió',
    decided:     'decidió',
    evaluated:   'evaluó',
    agreed:      'acordó',
    approved:    'aprobó',
    renamed:     'renombró',
    draft_saved: 'guardó el borrador de',
    draft_updated: 'actualizó el borrador de',
    version_created: 'abrió una versión de',
    proposed:    'propuso',
    proposal_updated: 'actualizó la propuesta de',
    plan_linked: 'ató al plan',
    promotion_authorized: 'autorizó una promoción en',
};

const RESOURCE_LABEL = {
    tasks:         'una tarea',
    tiers:         'un tier',
    projects:      'un proyecto',
    clients:       'un cliente',
    users:         'un usuario',
    teams:         'un equipo',
    repositories:  'un repositorio',
    project_teams: 'una asignación',
    documents:     'un documento',
    standards:     'un estándar',
    tokens:        'un token',
    findings:      'un hallazgo',
    // The newer kinds are named in the singular by their managers
    // (`finding.opened`, `review.decided`), and the label follows the kind.
    finding:       'un hallazgo',
    review:        'una revisión',
    document:      'un documento',
    plans:         'un plan',
    environments:  'un entorno',
    standard_version: 'una versión del estándar',
};

// Never returns null: an unknown kind shows its raw code rather than an empty
// cell. A movement nobody labelled yet is still a movement that happened.
export function kindLabel(kind) {
    if (TASK_KIND_LABEL[kind]) return TASK_KIND_LABEL[kind];

    const [resource, verb] = String(kind ?? '').split('.');
    if (verb && VERB_LABEL[verb]) {
        return `${VERB_LABEL[verb]} ${RESOURCE_LABEL[resource] ?? resource}`;
    }
    return kind ?? '—';
}

export function isTaskKind(kind) {
    return String(kind ?? '').startsWith('task.');
}

// A row of the pulse. `live` says it arrived through the channel, which is
// what makes the channel visible instead of merely claimed.
function row({ id, kind, at, projectId, tierId, taskId, taskTitle, taskState, userId, tokenId, live }) {
    return {
        id: Number(id),
        kind: kind ?? null,
        at: at ?? null,
        projectId: projectId ?? null,
        tierId: tierId ?? null,
        taskId: taskId ?? null,
        taskTitle: taskTitle ?? null,
        taskState: taskState ?? null,
        userId: userId ?? null,
        tokenId: tokenId ?? null,
        live: !!live,
        // True while the author of a live notice is being asked for. It is what
        // lets the list say "asking" instead of "nobody".
        authorPending: false,
    };
}

export function normalizeEvent(event) {
    return row({
        id: event.id,
        kind: event.kind,
        at: event.createdAt,
        projectId: event.project_id,
        tierId: event.tier_id,
        taskId: event.task_id,
        userId: event.user_id,
        tokenId: event.token_id,
        live: false,
    });
}

// Defensive about the task block: a notice for something that is not a task
// carries no task at all, and one with a long title may carry no title.
export function normalizeNotice(notice) {
    if (!notice || notice.id == null) return null;
    const task = notice.task ?? null;

    return row({
        id: notice.id,
        kind: notice.kind,
        at: notice.at ?? notice.createdAt ?? null,
        projectId: notice.project_id,
        tierId: notice.tier_id,
        taskId: task?.id ?? notice.task_id ?? null,
        taskTitle: task?.title ?? null,
        taskState: task?.state ?? null,
        // Absent in a notice by contract. Read anyway, for a replay that
        // travels as the row; otherwise `withAuthor` fills them in later.
        userId: notice.user_id,
        tokenId: notice.token_id,
        live: true,
    });
}

// Whether a notice row still needs its author looked up.
export function needsAuthor(item) {
    return !!item && item.userId == null && item.tokenId == null;
}

// Newest first, by id. The id is the row in `events`, so it orders the same
// way the database does — a clock would not, since the notice's `at` and the
// row's `createdAt` are written by different sides.
//
// A replayed notice for a row already on screen must not ERASE what that row
// knew: the notice has no author and may have lost its title, and the HTTP row
// or an earlier lookup already had them.
export function mergeRow(rows, incoming) {
    if (!incoming) return rows;
    const known = rows.find(item => item.id === incoming.id);
    const merged = known
        ? {
            ...incoming,
            userId: incoming.userId ?? known.userId,
            tokenId: incoming.tokenId ?? known.tokenId,
            taskTitle: incoming.taskTitle ?? known.taskTitle,
            authorPending: incoming.authorPending && known.userId == null && known.tokenId == null,
        }
        : incoming;
    const rest = rows.filter(item => item.id !== incoming.id);
    return [merged, ...rest].sort((a, b) => b.id - a.id).slice(0, FEED_SIZE);
}

// Writes the author a lookup found into the row it belongs to. `null` means
// the lookup failed or the event has none, and the row stops waiting.
export function withAuthor(rows, id, event) {
    return rows.map(item => (item.id === id
        ? {
            ...item,
            userId: event?.user_id ?? item.userId,
            tokenId: event?.token_id ?? item.tokenId,
            authorPending: false,
        }
        : item));
}
